/**
 * Moteur du contrôle de carrousel LinkedIn : pur, sans React.
 *
 * Deux familles de règles, et l'outil les distingue à l'écran :
 * - `official` : écrites dans l'aide LinkedIn « documents » (answer/97459) : formats PPT, PPTX, DOC,
 *   DOCX et PDF, 100 Mo et 300 pages maximum, pages de même taille, calques aplatis, titre du document,
 *   animations affichées en image fixe.
 * - `editorial` : repères NeverZero (ratio, poids de confort, nombre de pages, mots par page, corps de
 *   texte). LinkedIn ne publie ni dimensions ni nombre de pages conseillés pour un carrousel.
 */
import type { Severity } from '@/lib/postSimulator';

export type FileKind = 'pdf' | 'office' | 'other';

export const FILE_KINDS: Record<FileKind, string> = {
  pdf: 'PDF',
  office: 'PPT, PPTX, DOC ou DOCX',
  other: 'Autre (images, vidéo…)',
};

export const SIZE_PRESETS: { label: string; width: string; height: string }[] = [
  { label: '1080 × 1350 (portrait 4:5)', width: '1080', height: '1350' },
  { label: '1080 × 1080 (carré)', width: '1080', height: '1080' },
  { label: '1920 × 1080 (paysage 16:9)', width: '1920', height: '1080' },
  { label: '1080 × 1920 (vertical 9:16)', width: '1080', height: '1920' },
];

export interface CarouselInputs {
  kind: FileKind | null;
  pages: string;
  sizeMb: string;
  width: string;
  height: string;
  fontPx: string;
  sameSize: boolean;
  flattened: boolean;
  titled: boolean;
  slides: string;
}

export const EMPTY_INPUTS: CarouselInputs = {
  kind: null,
  pages: '',
  sizeMb: '',
  width: '',
  height: '',
  fontPx: '',
  sameSize: true,
  flattened: true,
  titled: true,
  slides: '',
};

export type CheckSource = 'official' | 'editorial';

export interface CarouselCheck {
  id: string;
  source: CheckSource;
  severity: Severity;
  title: string;
  detail: string;
  fix?: string;
}

export type Verdict = 'incomplete' | 'blocked' | 'fix' | 'ready';

export interface CarouselResult {
  verdict: Verdict;
  headline: string;
  summary: string;
  checks: CarouselCheck[];
  problems: number;
  warnings: number;
  passed: number;
  slideWords: number[];
  /** Corps de texte minimal en pixels, à la largeur saisie, pour rester lisible sur téléphone. */
  minFontPx: number | null;
}

/** Largeur d'affichage d'un document dans le fil mobile, en pixels CSS : un ordre de grandeur, pas une mesure LinkedIn. */
export const PHONE_WIDTH = 360;
/** Plus petit corps de texte courant lisible sans zoom, en pixels CSS. Repère éditorial. */
export const MIN_RENDERED_PX = 11;
export const MAX_MB = 100;
export const MAX_PAGES = 300;

export function parseNumber(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Découpe le texte collé en pages : lignes `---` si présentes, sinon blocs séparés par une ligne vide. */
export function splitSlides(text: string): string[] {
  const normalized = text.replace(/\r\n?/g, '\n').trim();
  if (!normalized) return [];
  const parts = /^\s*-{3,}\s*$/m.test(normalized) ? normalized.split(/^\s*-{3,}\s*$/m) : normalized.split(/\n\s*\n/);
  return parts.map((part) => part.trim()).filter(Boolean);
}

export function countWords(text: string): number {
  return (text.match(/[\p{L}\p{N}][\p{L}\p{N}'’%€.,-]*/gu) ?? []).length;
}

function frNumber(n: number, digits = 0): string {
  return n.toLocaleString('fr-FR', { maximumFractionDigits: digits });
}

const CTA_WORDS =
  /\b(?:abonn\w*|suivez|suis-moi|commentez|commente|écrivez|écris|contactez|contacte|message|dm|réponds|répondez|dites|dis-moi|partagez|partage|enregistrez|sauvegardez|téléchargez|réservez|rejoignez|lien|et vous|votre avis|qu'en pensez|retrouvez|follow|comment)\b/i;

export function ratioLabel(width: number, height: number): string {
  const r = width / height;
  if (Math.abs(r - 1) < 0.02) return 'carré 1:1';
  if (Math.abs(r - 0.8) < 0.02) return 'portrait 4:5';
  if (Math.abs(r - 9 / 16) < 0.02) return 'vertical 9:16';
  if (Math.abs(r - 16 / 9) < 0.02) return 'paysage 16:9';
  if (Math.abs(r - 4 / 3) < 0.02) return 'paysage 4:3';
  return r > 1 ? 'paysage' : 'portrait';
}

export function evaluate(input: CarouselInputs): CarouselResult {
  const pages = parseNumber(input.pages);
  const sizeMb = parseNumber(input.sizeMb);
  const width = parseNumber(input.width);
  const height = parseNumber(input.height);
  const fontPx = parseNumber(input.fontPx);
  const slides = splitSlides(input.slides);
  const slideWords = slides.map(countWords);
  const checks: CarouselCheck[] = [];

  const empty: CarouselResult = {
    verdict: 'incomplete',
    headline: '',
    summary: '',
    checks,
    problems: 0,
    warnings: 0,
    passed: 0,
    slideWords,
    minFontPx: null,
  };
  const hasAny = input.kind !== null || pages !== null || sizeMb !== null || width !== null || slides.length > 0;
  if (!hasAny) return empty;

  // 1. Format de fichier (officiel).
  if (input.kind === 'pdf') {
    checks.push({
      id: 'format',
      source: 'official',
      severity: 'ok',
      title: 'Format PDF',
      detail: 'LinkedIn recommande de convertir en PDF quand c’est possible : c’est le format qui donne la meilleure qualité d’import.',
    });
  } else if (input.kind === 'office') {
    checks.push({
      id: 'format',
      source: 'official',
      severity: 'warning',
      title: 'Format accepté, mais pas idéal',
      detail: 'PPT, PPTX, DOC et DOCX sont acceptés. LinkedIn conseille pourtant de les convertir en PDF pour la meilleure qualité, et la mise en page peut changer d’un logiciel à l’autre.',
      fix: 'Exportez en PDF depuis PowerPoint, Canva ou Google Slides, puis ouvrez le PDF pour vérifier avant de publier.',
    });
  } else if (input.kind === 'other') {
    checks.push({
      id: 'format',
      source: 'official',
      severity: 'problem',
      title: 'Ce format ne passe pas comme carrousel',
      detail: 'Un carrousel LinkedIn est un document : PDF, PPT, PPTX, DOC ou DOCX. Des images séparées forment un post multi-images, pas un document à feuilleter.',
      fix: 'Rassemblez vos pages dans un seul PDF (export Canva, PowerPoint ou Google Slides).',
    });
  }

  // 2. Nombre de pages : plafond officiel, puis repère éditorial.
  if (pages !== null) {
    if (!Number.isInteger(pages) || pages < 1) {
      checks.push({
        id: 'pages',
        source: 'official',
        severity: 'problem',
        title: 'Nombre de pages invalide',
        detail: 'Saisissez un nombre entier de pages, au moins 1.',
      });
    } else if (pages > MAX_PAGES) {
      checks.push({
        id: 'pages',
        source: 'official',
        severity: 'problem',
        title: `${frNumber(pages)} pages : au-dessus du plafond de ${MAX_PAGES}`,
        detail: `LinkedIn refuse un document de plus de ${MAX_PAGES} pages. Vous dépassez de ${frNumber(pages - MAX_PAGES)}.`,
        fix: 'Découpez en plusieurs documents. Un carrousel qui se lit d’une traite tient en général sur bien moins de pages.',
      });
    } else if (pages === 1) {
      checks.push({
        id: 'pages',
        source: 'editorial',
        severity: 'warning',
        title: 'Une seule page',
        detail: 'Un document d’une page ne se feuillette pas : ce n’est pas un carrousel, c’est une image.',
        fix: 'Publiez-la comme image, ou découpez le contenu sur 5 à 10 pages.',
      });
    } else if (pages < 5) {
      checks.push({
        id: 'pages',
        source: 'editorial',
        severity: 'warning',
        title: `${pages} pages : un carrousel très court`,
        detail: 'Peu de pages à feuilleter : le lecteur a fini avant d’avoir pris l’habitude de balayer. Aucun minimum n’est imposé par LinkedIn, c’est un repère NeverZero.',
        fix: 'Vérifiez que chaque page apporte une idée de plus. Sinon, une image seule fait le même travail.',
      });
    } else if (pages > 20) {
      checks.push({
        id: 'pages',
        source: 'editorial',
        severity: pages > 30 ? 'problem' : 'warning',
        title: `${pages} pages : long pour un carrousel`,
        detail: 'Le plafond officiel est de 300 pages, mais la lecture s’arrête bien avant. Plus le document est long, plus la dernière page, où se trouve l’appel à l’action, est vue par peu de monde.',
        fix: 'Visez 5 à 15 pages. Au-delà, mettez le meilleur contenu en tête ou coupez en deux documents.',
      });
    } else {
      checks.push({
        id: 'pages',
        source: 'editorial',
        severity: 'ok',
        title: `${pages} pages : une longueur qui se lit`,
        detail: 'Sous le plafond de 300 pages, et dans la plage où un carrousel est lu en entier.',
      });
    }
  }

  // 3. Poids : plafond officiel, puis confort de chargement.
  if (sizeMb !== null) {
    if (sizeMb <= 0) {
      checks.push({
        id: 'weight',
        source: 'official',
        severity: 'problem',
        title: 'Poids invalide',
        detail: 'Saisissez le poids du fichier en Mo, supérieur à 0.',
      });
    } else if (sizeMb > MAX_MB) {
      checks.push({
        id: 'weight',
        source: 'official',
        severity: 'problem',
        title: `${frNumber(sizeMb, 1)} Mo : au-dessus du plafond de ${MAX_MB} Mo`,
        detail: `LinkedIn refuse un document de plus de ${MAX_MB} Mo. Vous dépassez de ${frNumber(sizeMb - MAX_MB, 1)} Mo.`,
        fix: 'Compressez les images du document (export « taille réduite » dans Canva, ou PDF optimisé) avant l’envoi.',
      });
    } else if (sizeMb > 25) {
      checks.push({
        id: 'weight',
        source: 'editorial',
        severity: 'warning',
        title: `${frNumber(sizeMb, 1)} Mo : accepté, mais lourd`,
        detail: 'Sous le plafond de 100 Mo, mais un document lourd se charge lentement en 4G, et un lecteur qui attend part. Le seuil de 25 Mo est un repère NeverZero.',
        fix: 'Compressez les images : un carrousel de texte et de schémas tient très bien sous 10 Mo.',
      });
    } else {
      checks.push({
        id: 'weight',
        source: 'editorial',
        severity: 'ok',
        title: `${frNumber(sizeMb, 1)} Mo : léger`,
        detail: 'Bien sous le plafond de 100 Mo, il se chargera vite même en mobilité.',
      });
    }
  }

  // 4. Dimensions : ratio et netteté (repères éditoriaux, LinkedIn n'en publie pas).
  let minFontPx: number | null = null;
  if (width !== null || height !== null) {
    if (width === null || height === null || width <= 0 || height <= 0) {
      checks.push({
        id: 'ratio',
        source: 'editorial',
        severity: 'warning',
        title: 'Dimensions incomplètes',
        detail: 'Renseignez la largeur et la hauteur d’une page, en pixels, pour contrôler le format.',
      });
    } else {
      const ratio = width / height;
      const label = ratioLabel(width, height);
      minFontPx = Math.ceil((MIN_RENDERED_PX * width) / PHONE_WIDTH);
      if (ratio > 1.05) {
        checks.push({
          id: 'ratio',
          source: 'editorial',
          severity: 'warning',
          title: `Format ${label} : petit sur un téléphone`,
          detail: `Une page ${frNumber(width)} × ${frNumber(height)} s’affiche en bandeau dans le fil mobile : elle occupe peu de hauteur d’écran et le texte y paraît plus petit qu’en portrait. LinkedIn ne publie pas de format conseillé ; c’est le repère NeverZero.`,
          fix: 'Passez en portrait 4:5 (1080 × 1350) ou en carré (1080 × 1080). Un export Canva permet de changer de format sans tout refaire.',
        });
      } else if (ratio < 0.74) {
        checks.push({
          id: 'ratio',
          source: 'editorial',
          severity: 'warning',
          title: `Format ${label} : plus haut que le portrait 4:5`,
          detail: 'LinkedIn ne dit pas comment il traite un format plus étiré que le 4:5 : il peut être réduit pour tenir à l’écran, donc plus petit que prévu. Vérifiez l’aperçu avant de publier.',
          fix: 'Le portrait 4:5 (1080 × 1350) est le format le plus haut dont le rendu est largement éprouvé.',
        });
      } else {
        checks.push({
          id: 'ratio',
          source: 'editorial',
          severity: 'ok',
          title: `Format ${label}`,
          detail: ratio < 0.95
            ? 'Un portrait occupe presque toute la largeur et une grande part de la hauteur du fil mobile : c’est le format qui laisse le plus de place au texte.'
            : 'Le carré s’affiche pleine largeur et reste lisible, avec un peu moins de hauteur qu’un portrait 4:5.',
        });
      }
      if (width < 1000) {
        checks.push({
          id: 'sharp',
          source: 'editorial',
          severity: 'warning',
          title: `Largeur de ${frNumber(width)} px : risque de flou`,
          detail: 'Sur un écran haute densité, une page de moins de 1 000 px de large est agrandie et perd en netteté, surtout pour le texte fin. C’est un repère NeverZero.',
          fix: 'Exportez à 1080 px de large au moins.',
        });
      }
    }
  }

  // 5. Corps de texte rendu sur téléphone (calcul à partir de la largeur de page et du corps saisi).
  if (fontPx !== null && width !== null && width > 0) {
    const rendered = (fontPx * PHONE_WIDTH) / width;
    const needed = Math.ceil((MIN_RENDERED_PX * width) / PHONE_WIDTH);
    if (fontPx <= 0) {
      checks.push({
        id: 'font',
        source: 'editorial',
        severity: 'warning',
        title: 'Corps de texte invalide',
        detail: 'Saisissez le corps du texte courant en pixels, supérieur à 0.',
      });
    } else if (rendered < MIN_RENDERED_PX) {
      checks.push({
        id: 'font',
        source: 'editorial',
        severity: 'problem',
        title: `Texte de ${frNumber(fontPx)} px : environ ${frNumber(rendered, 1)} px à l’écran`,
        detail: `Sur un téléphone, une page de ${frNumber(width)} px de large est affichée sur environ ${PHONE_WIDTH} px. Votre texte y paraît à ${frNumber(rendered, 1)} px, sous les ${MIN_RENDERED_PX} px qu’on lit sans zoomer. Les deux chiffres sont des repères NeverZero.`,
        fix: `À ${frNumber(width)} px de large, passez le texte courant à ${frNumber(needed)} px au minimum.`,
      });
    } else {
      checks.push({
        id: 'font',
        source: 'editorial',
        severity: 'ok',
        title: `Texte de ${frNumber(fontPx)} px : environ ${frNumber(rendered, 1)} px à l’écran`,
        detail: `Au-dessus de ${MIN_RENDERED_PX} px une fois la page réduite à la largeur d’un téléphone.`,
      });
    }
  }

  // 6. Règles de préparation du PDF (officielles).
  if (!input.sameSize) {
    checks.push({
      id: 'samesize',
      source: 'official',
      severity: 'problem',
      title: 'Pages de tailles différentes',
      detail: 'LinkedIn demande que toutes les pages d’un PDF aient la même taille.',
      fix: 'Redimensionnez chaque page au même format avant l’export, ou exportez depuis un outil qui fixe un format unique.',
    });
  }
  if (!input.flattened) {
    checks.push({
      id: 'layers',
      source: 'official',
      severity: 'problem',
      title: 'PDF à calques multiples',
      detail: 'LinkedIn demande qu’un PDF à calques soit aplati ou fusionné avant l’envoi.',
      fix: 'Exportez en « PDF aplati » ou réimprimez le fichier en PDF depuis votre lecteur.',
    });
  }
  if (!input.titled) {
    checks.push({
      id: 'title',
      source: 'official',
      severity: 'warning',
      title: 'Document sans titre',
      detail: 'LinkedIn demande un titre de document, qui s’affiche au-dessus du carrousel. Le document ne pourra plus être remplacé après publication.',
      fix: 'Préparez un titre clair avant l’envoi : il décrit le sujet à ceux qui ne l’ouvrent pas.',
    });
  }
  if (checks.length > 0 && input.sameSize && input.flattened && input.titled && (input.kind === 'pdf' || input.kind === 'office')) {
    checks.push({
      id: 'prep',
      source: 'official',
      severity: 'ok',
      title: 'Préparation du document conforme',
      detail: 'Pages de même taille, calques aplatis, titre prévu. Rappel : les vidéos et animations d’un document s’affichent en image fixe.',
    });
  }

  // 7. Texte des pages (si collé).
  if (slides.length > 0) {
    const total = slideWords.reduce((a, b) => a + b, 0);
    const avg = total / slides.length;
    const bodyWords = slideWords.slice(1, Math.max(1, slides.length - 1));
    const maxBody = bodyWords.length > 0 ? Math.max(...bodyWords) : 0;
    const denseIdx = bodyWords.indexOf(maxBody) + 1;
    const cover = slideWords[0] ?? 0;

    if (cover > 20) {
      checks.push({
        id: 'cover',
        source: 'editorial',
        severity: 'problem',
        title: `Couverture de ${cover} mots`,
        detail: 'La couverture est ce que le lecteur voit avant de décider de feuilleter. À cette longueur, elle se lit comme une page de contenu, pas comme une promesse.',
        fix: 'Gardez une promesse de 6 à 12 mots : le résultat que le lecteur obtient, avec un chiffre si vous en avez un.',
      });
    } else if (cover > 12) {
      checks.push({
        id: 'cover',
        source: 'editorial',
        severity: 'warning',
        title: `Couverture de ${cover} mots`,
        detail: 'Lisible, mais un peu longue pour un titre qu’on lit en une seconde.',
        fix: 'Coupez à 12 mots ou moins ; déplacez le détail en page 2.',
      });
    } else if (cover < 3) {
      checks.push({
        id: 'cover',
        source: 'editorial',
        severity: 'warning',
        title: `Couverture de ${cover} mot${cover > 1 ? 's' : ''}`,
        detail: 'Trop courte pour dire ce que le lecteur va gagner à feuilleter.',
        fix: 'Écrivez la promesse complète : le sujet et le résultat, en 6 à 12 mots.',
      });
    } else {
      checks.push({
        id: 'cover',
        source: 'editorial',
        severity: 'ok',
        title: `Couverture de ${cover} mots`,
        detail: 'Assez courte pour être lue d’un coup d’œil, assez longue pour porter une promesse.',
      });
    }

    if (maxBody > 90) {
      checks.push({
        id: 'density',
        source: 'editorial',
        severity: 'problem',
        title: `Page ${denseIdx + 1} : ${maxBody} mots`,
        detail: `Moyenne de ${frNumber(avg, 0)} mots par page. Une page de ${maxBody} mots est un paragraphe, pas une diapositive : sur téléphone, elle impose de réduire le corps et personne ne la lit en entier.`,
        fix: 'Scindez cette page en deux, ou gardez une idée et une phrase d’appui. Repère NeverZero : 60 mots par page au plus.',
      });
    } else if (maxBody > 60 || avg > 45) {
      checks.push({
        id: 'density',
        source: 'editorial',
        severity: 'warning',
        title: maxBody > 60 ? `Page ${denseIdx + 1} : ${maxBody} mots` : `${frNumber(avg, 0)} mots par page en moyenne`,
        detail: 'Dense pour un écran de téléphone : le corps du texte doit rester grand, ce qui limite ce qui tient dans la page.',
        fix: 'Visez 15 à 40 mots par page, une idée à la fois.',
      });
    } else {
      checks.push({
        id: 'density',
        source: 'editorial',
        severity: 'ok',
        title: maxBody > 0 ? `${frNumber(avg, 0)} mots par page en moyenne, ${maxBody} au maximum` : `${frNumber(avg, 0)} mots par page en moyenne`,
        detail: 'Un texte court par page permet de garder un gros corps de texte, donc lisible sur un téléphone.',
      });
    }

    if (slides.length >= 3) {
      const last = slides[slides.length - 1];
      if (CTA_WORDS.test(last)) {
        checks.push({
          id: 'cta',
          source: 'editorial',
          severity: 'ok',
          title: 'La dernière page invite à agir',
          detail: 'Elle contient un appel : commenter, s’abonner, écrire. C’est la page vue par le moins de monde, elle doit donc valoir le détour.',
        });
      } else {
        checks.push({
          id: 'cta',
          source: 'editorial',
          severity: 'warning',
          title: 'Pas d’appel à agir en dernière page',
          detail: 'Le lecteur qui arrive au bout est le plus engagé, et rien ne lui dit quoi faire ensuite.',
          fix: 'Terminez par une demande unique : une question à commenter, ou « écrivez-moi “carrousel” en message ».',
        });
      }
    }

    if (pages !== null && Number.isInteger(pages) && slides.length !== pages) {
      checks.push({
        id: 'count',
        source: 'editorial',
        severity: 'warning',
        title: `Vous avez collé ${slides.length} pages, pour ${pages} annoncées`,
        detail: 'Le contrôle du texte porte sur les pages collées. Une page sans texte (un visuel) ne se colle pas : c’est normal. Sinon, un séparateur est peut-être manquant.',
        fix: 'Séparez chaque page par une ligne « --- », ou par une ligne vide.',
      });
    }
  }

  const problems = checks.filter((c) => c.severity === 'problem').length;
  const warnings = checks.filter((c) => c.severity === 'warning').length;
  const passed = checks.filter((c) => c.severity === 'ok').length;

  const officialBlock = checks.some((c) => c.source === 'official' && c.severity === 'problem');
  let verdict: Verdict;
  let headline: string;
  let summary: string;
  if (checks.length === 0) {
    return { ...empty, minFontPx };
  }
  if (officialBlock) {
    verdict = 'blocked';
    headline = 'LinkedIn risque de refuser ce document';
    summary = `${problems} point${problems > 1 ? 's' : ''} à corriger, dont au moins une règle écrite dans l’aide LinkedIn.`;
  } else if (problems > 0) {
    verdict = 'fix';
    headline = 'Il passera, mais ne sera pas lu';
    summary = `Le document respecte les règles de LinkedIn. ${problems} point${problems > 1 ? 's' : ''} de lisibilité à corriger avant de publier.`;
  } else if (warnings > 0) {
    verdict = 'fix';
    headline = 'Prêt, avec quelques réglages';
    summary = `Aucun blocage. ${warnings} point${warnings > 1 ? 's' : ''} à surveiller, ${passed} validé${passed > 1 ? 's' : ''}.`;
  } else {
    verdict = 'ready';
    headline = 'Prêt à publier';
    summary = `${passed} contrôle${passed > 1 ? 's' : ''} validé${passed > 1 ? 's' : ''}, aucun point à corriger.`;
  }

  // Les problèmes d'abord, puis les alertes, puis ce qui passe.
  const order: Record<Severity, number> = { problem: 0, warning: 1, ok: 2 };
  const sorted = [...checks].sort((a, b) => order[a.severity] - order[b.severity]);

  return { verdict, headline, summary, checks: sorted, problems, warnings, passed, slideWords, minFontPx };
}

export function reportText(result: CarouselResult): string {
  const label: Record<Severity, string> = { problem: 'À corriger', warning: 'À surveiller', ok: 'OK' };
  const src: Record<CheckSource, string> = { official: 'règle LinkedIn', editorial: 'repère NeverZero' };
  const head = `${result.headline}\n${result.summary}`;
  const body = result.checks
    .map((c) => `[${label[c.severity]}] ${c.title} (${src[c.source]})\n${c.detail}${c.fix ? `\n→ ${c.fix}` : ''}`)
    .join('\n\n');
  return `${head}\n\n${body}`;
}
